import dayjs from 'dayjs';
import { convertReceiptEventToCoupaReceiptEvent } from '../../common/helperFunctions/coupa';
import { PackageItem } from '../../common/helperFunctions/sscc';
import { CoupaReceiptEvent, ReceiptLine } from '../../common/types';
import { ReceiptEventPackageItemProps, ReceiptEventPackageProps } from '../../datastore/ssccs';
import { isSubLine, isSummaryLine } from './utils';

/**
 * Builds Coupa receipt line items from receipt lines.
 * Summary lines are excluded, as Coupa only accepts actual receipt items.
 */
export const buildCoupaLines = (lines: ReceiptLine[]): ReceiptEventPackageItemProps[] => {
  return lines
    .filter((line) => !isSummaryLine(line))
    .map((line) => ({
      orderLineId: line.originalPoLineId ?? line.id!,
      asnLineId: line.asnLineId ?? undefined,
      partId: line.partId!,
      sourcePartId: line.sourcePartId ?? '',
      quantityOrdered: line.quantityOrdered ?? null,
      quantityReceived: line.quantityReceived ?? null,
      description: line.description ?? '',
      purchaseType: line.purchaseType ?? '',
      receivingWarehouseId: line.receivingWarehouseId ?? null,
      receivingWarehouseDefaultLocationId: line.receivingWarehouseDefaultLocationId ?? null,
      inTransitWarehouseId: line.inTransitWarehouseId ?? null,
      inTransitWarehouseLocationId: line.inTransitWarehouseLocationId ?? null,
      receipt: dayjs()
    }))
    .filter((item) => item.quantityReceived); // Exclude items with zero quantity received
};

/**
 * Builds package (SSCC) item lines.
 * Sub-lines are excluded, and summary lines use aggregated received quantities.
 */
export const buildPackageLines = (
  lines: ReceiptLine[],
  summaryLineQuantityReceivedQuantitySet: Map<number, number>
): PackageItem[] => {
  return lines
    .filter((line) => !isSubLine(line))
    .map((line) => ({
      orderLineId: line.originalPoLineId ?? line.id!,
      partId: line.partId!,
      sourcePartId: line.sourcePartId ?? '',
      quantityOrdered: line.quantityOrdered ?? null,
      quantityReceived: isSummaryLine(line)
        ? (summaryLineQuantityReceivedQuantitySet.get(line.summaryId || 0) ?? 0)
        : (line.quantityReceived ?? null),
      description: line.description ?? '',
      purchaseType: line.purchaseType ?? '',
      receivingWarehouseId: line.receivingWarehouseId ?? null,
      receivingWarehouseDefaultLocationId: line.receivingWarehouseDefaultLocationId ?? null,
      inTransitWarehouseId: line.inTransitWarehouseId ?? null,
      inTransitWarehouseLocationId: line.inTransitWarehouseLocationId ?? null
    }))
    .filter((item) => item.quantityReceived); // Exclude items with zero quantity received
};

/**
 * Generates a unique ASN number using a prefix and a shortened UUID.
 */
export const generateAsn = (prefix = 'MRL'): string => {
  return `${prefix}${crypto.randomUUID().substring(0, 8)}`;
};

/**
 * Generates a Coupa receipt event payload from package and receipt line data.
 */
export const coupaReceiptEventGenerator = ({
  latestPackage,
  coupaLines,
  userName,
  userLocationName,
  asn,
  isDirectReceipt = false
}: {
  latestPackage: ReceiptEventPackageProps;
  coupaLines: ReceiptEventPackageItemProps[];
  userName: string;
  userLocationName?: string;
  asn: string;
  isDirectReceipt?: boolean;
}): CoupaReceiptEvent => {
  const coupaPackage: ReceiptEventPackageProps = {
    ...latestPackage,
    items: coupaLines
  };

  return convertReceiptEventToCoupaReceiptEvent(coupaPackage, userName, userLocationName, asn, isDirectReceipt);
};
